import React, { useCallback, ReactNode } from "react";
import styled from "styled-components";
import { AiOutlineSearch } from "react-icons/ai";
import Downshift from "downshift";
import { Image as ImageClass } from "@ludens-reklame/rubics-sdk";
import TextInput from "../../style-guide/Inputs/TextInput";
import { useSearch } from "../../hooks/useApi";
import { SerializedResult } from "../../types/apiResponses";
import Card from "../../style-guide/Card/Card";
import Text from "../../style-guide/Text/Text";
import localize from "../../util/localize";
import { searchResultType } from "../../constants/localizations";
import BusyBoy from "../BusyBoy/BusyBoy";
import { SerializedType } from "../../constants/api";
import useThrottle from "../../hooks/useThrottle";
import { Elements, SimpleElement } from "../../style-guide/Elements/Elements";
import { useHistory } from "react-router-dom";
import useApiUrl from "../../hooks/useApiUrl";
import { Flex, FlexKid } from "../../style-guide/Flex/Flex";
import Image from "../../style-guide/Image/Image";
import { Dotter, Dot } from "../../style-guide/Dotter/Dotter";
import convertImage from "../../util/convertImage";

const Wrapper = styled.div`
  position: relative;
  z-index: 5;
`;

const Results = styled.div`
  position: absolute;
  left: 0;
  right: 0;
`;

const Search: React.FC = () => {
  const history = useHistory();
  const url = useApiUrl(["search"]);

  const queryOpts = {
    endpoint: url,
    queryParams: {}
  };

  const { search, loading, results, hasSearched } =
    useSearch<SerializedResult>(queryOpts);
  const throttledSearch = useThrottle(search, 500);

  const handleSearch = useCallback(
    (value: string) => {
      if (value) {
        throttledSearch({
          ...queryOpts,
          queryParams: {
            ...queryOpts.queryParams,
            q: value
          }
        });
      }
    },
    [queryOpts, throttledSearch]
  );

  return (
    <Downshift
      itemToString={(i) => (i ? (i as SerializedResult).label : "")}
      onSelect={(rawItem) => {
        const item = rawItem as SerializedResult;

        switch (item.type) {
          case SerializedType.Product:
            history.push("/butikk/produkter/" + item._id);

            break;
          case SerializedType.Category:
            history.push("/innhold/kategorier/" + item._id);

            break;
          case SerializedType.ProductPage:
            history.push("/innhold/produktsider/" + item._id);

            break;
          case SerializedType.Order:
            history.push("/butikk/ordrer/" + item._id);

            break;
          case SerializedType.App:
            history.push("/apper/" + item._id);

            break;
          case SerializedType.Template:
            history.push("/innstillinger/maler/liste/" + item._id);

            break;
          case SerializedType.Component:
            history.push("/innhold/komponenter/" + item._id);

            break;
          default:
            history.push("/innhold/sider/" + item._id);

            break;
        }
      }}
      onInputValueChange={handleSearch}
    >
      {({
        inputValue,
        getInputProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        isOpen
      }) => (
        <div>
          <Wrapper>
            <TextInput
              bigger
              placeholder="Søk etter innhold på nettstedet ditt…"
              icon={<AiOutlineSearch />}
              {...getInputProps()}
            />
            {isOpen && inputValue && (
              <>
                <Results>
                  <Card>
                    <Elements dontCompensateMargin rounded {...getMenuProps()}>
                      <BusyBoy busy={loading}>
                        {hasSearched && !loading && results.length === 0 ? (
                          <SimpleElement biggerSpacing>
                            <Text variant="body3">
                              Ingen treff på{" "}
                              <em>
                                <strong>{inputValue}</strong>
                              </em>
                            </Text>
                          </SimpleElement>
                        ) : (
                          results.map((r, k) => (
                            <SimpleElement
                              {...getItemProps({
                                key: r._id,
                                index: k,
                                item: r,
                                className:
                                  highlightedIndex === k ? "active" : undefined
                              })}
                              biggerSpacing
                            >
                              <Flex>
                                <FlexKid flex={1}>
                                  <Text variant="body2">
                                    <a>{r.label}</a>
                                  </Text>
                                  <Text variant="body3">
                                    <Dotter>
                                      <Dot>
                                        {localize(searchResultType, r.type)}
                                      </Dot>
                                      {renderMeta(r.type, r.meta)}
                                    </Dotter>
                                  </Text>
                                </FlexKid>
                                {r.image && r.image.ref && (
                                  <FlexKid spaceLeft>
                                    <Image
                                      src={ImageClass.createUrl({
                                        image: convertImage(r.image),
                                        transforms:
                                          r.type === SerializedType.App
                                            ? "h_82,w_82,c_pad"
                                            : "h_82,w_82,c_fill"
                                      })}
                                      alt={r.label}
                                      noBorderRadius={
                                        r.type === SerializedType.App
                                      }
                                    />
                                  </FlexKid>
                                )}
                              </Flex>
                            </SimpleElement>
                          ))
                        )}
                      </BusyBoy>
                    </Elements>
                  </Card>
                </Results>
              </>
            )}
          </Wrapper>
        </div>
      )}
    </Downshift>
  );
};

function renderMeta(type: SerializedType, meta?: any): ReactNode {
  if (!meta) {
    return null;
  }

  switch (type) {
    case SerializedType.Page:
    case SerializedType.Category:
    case SerializedType.Product:
      if (Array.isArray(meta) && meta.length > 0) {
        return <Dot>{meta.map((p) => p.title).join(" > ")}</Dot>;
      }

      break;
    case SerializedType.App:
      return meta ? <Dot>{meta}</Dot> : null;
  }

  return null;
}

export default Search;
